import { useRef, useState } from "preact/hooks"

import { dragDelta, dragOrigin, passouDoLimiar, type Point } from "../lib/drag"
import { IconChat, IconClose } from "./icons"

type Props = {
  open: boolean
  unread: number
  draggable: boolean
  onToggle: () => void
  onDrag: (phase: "start" | "move" | "end", dx: number, dy: number) => void
}

/**
 * A bolha.
 *
 * O arrasto NAO move nada aqui: um iframe nao consegue se reposicionar. O que
 * este componente faz e traduzir o gesto em mensagens `drag` para o host, que e
 * quem tem a geometria do elemento. Ver §6.1 do planejamento.
 */
export function Launcher({ open, unread, draggable, onToggle, onDrag }: Props) {
  // `useRef` e nao variavel local. O corpo do componente roda de novo a cada
  // re-render, e uma variavel local voltaria a zero no meio do gesto — inclusive
  // por causa do `setDragging` logo abaixo, que e ele proprio um re-render.
  const origin = useRef<Point | null>(null)
  const moved = useRef(false)
  const [dragging, setDragging] = useState(false)

  const onPointerDown = (event: PointerEvent) => {
    if (!draggable || event.button !== 0) return
    origin.current = dragOrigin(event)
    moved.current = false
    ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
    onDrag("start", 0, 0)
  }

  const onPointerMove = (event: PointerEvent) => {
    const start = origin.current
    if (!start) return
    const delta = dragDelta(start, event)
    // Limiar: sem ele, todo arrasto termina abrindo o chat.
    if (!moved.current && !passouDoLimiar(delta)) return
    if (!moved.current) {
      moved.current = true
      setDragging(true)
    }
    onDrag("move", delta.x, delta.y)
  }

  const endDrag = (event: PointerEvent) => {
    if (!origin.current) return
    origin.current = null
    setDragging(false)
    onDrag("end", 0, 0)
    const target = event.currentTarget as HTMLElement
    if (target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId)
    }
  }

  return (
    <button
      type="button"
      class={`launcher${draggable ? " draggable" : ""}${dragging ? " dragging" : ""}`}
      aria-expanded={open}
      aria-label={
        open
          ? "Fechar conversa"
          : draggable
            ? "Abrir conversa. Arraste para mover o ícone."
            : "Abrir conversa"
      }
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={() => {
        // Soltar nao e clicar.
        if (moved.current) {
          moved.current = false
          return
        }
        onToggle()
      }}
    >
      {!open && unread > 0 && (
        <span class="badge">{unread > 9 ? "9+" : String(unread)}</span>
      )}
      {open ? <IconClose /> : <IconChat />}
      {!open && unread > 0 && (
        <span class="sr-only">{unread} mensagens não lidas</span>
      )}
    </button>
  )
}
